// ===============================
// NOVAS FUNÇÕES MOTOR V9
// ===============================

/**
 * Busca instâncias de workflow (motor V9) para uma organização
 */
export async function getWorkflowInstances(organizationId: string) {
  if (!organizationId || organizationId.length !== 36) throw new Error('organizationId inválido');
  const { data, error } = await supabase
    .from('workflow_instances_v9')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('status', 'active')
    .order('started_at', { ascending: false });
  if (error) throw error;
  return data;
}

/**
 * Busca uma instância específica de workflow (motor V9) por ID
 */
export async function getWorkflowInstanceById(instanceId: string) {
  if (!instanceId || instanceId.length !== 36) throw new Error('instanceId inválido');
  const { data, error } = await supabase
    .from('workflow_instances_v9')
    .select('*')
    .eq('id', instanceId)
    .single();
  if (error) throw error;
  return data;
}

/**
 * Busca tasks de uma instância de workflow (motor V9)
 */
export async function getWorkflowInstanceTasks(instanceId: string) {
  if (!instanceId || instanceId.length !== 36) throw new Error('instanceId inválido');
  const { data, error } = await supabase
    .from('workflow_tasks_v9')
    .select('*')
    .eq('instance_id', instanceId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}
/**
 * 🔄 WORKFLOW SERVICE
 * 
 * Busca dados operacionais de workflow_tasks_v9 e views relacionadas
 * para exibição no Dashboard de Decisão
 */

import { supabase } from '../lib/supabaseClient';

// ============================================================================
// TIPOS
// ============================================================================

export interface WorkflowTask {
  id: string;
  workflow_id: string;
  assigned_to: string | null;
  organization_id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  stage_name: string;
  task_name: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  sla_deadline: string | null;
  is_overdue: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

export interface OperationalMetrics {
  tasks_overdue: number;
  sla_rate_7d: number;
  sla_rate_30d: number;
  avg_wip_per_user: number;
  throughput_7d: number;
  most_critical_stage: string | null;
  tasks_due_today: number;
}

export interface MyTasksSummary {
  pending_tasks: Array<{
    id: string;
    task_name: string;
    stage_name: string;
    sla_deadline: string | null;
    is_overdue: boolean;
    hours_until_deadline: number | null;
  }>;
  my_overdue_count: number;
  my_sla_rate_7d: number;
}

export interface StagePerformance {
  stage_name: string;
  avg_wip: number;
  avg_cycle_time_hours: number;
  sla_rate: number;
  /** False quando a etapa não tem conclusão no período: SLA 0% não é desvio. */
  sla_sampled?: boolean;
  tasks_count: number;
}

export interface UserPerformance {
  user_id: string;
  user_email: string;
  sla_rate_7d: number;
  avg_wip: number;
  tasks_completed_7d: number;
  tasks_overdue: number;
}

export interface DailyMetricPoint {
  date: string;
  label: string;
  value: number;
  total?: number;
  onTime?: number;
  late?: number;
}

// ============================================================================
// FUNÇÕES
// ============================================================================

/**
 * Busca métricas operacionais gerais da organização
 */
export async function getOperationalMetrics(organizationId: string): Promise<OperationalMetrics> {
  try {
    // Buscar tasks ativas
    const { data: tasks, error: tasksError } = await supabase
      .from('workflow_tasks_v9')
      .select('id, status, is_overdue, sla_deadline, completed_at, assigned_to, stage_id, stage:workflow_stages_v9(name)')
      .eq('organization_id', organizationId)
      .in('status', ['pending', 'in_progress']);

    if (tasksError) throw tasksError;

    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Tasks concluídas nos últimos 7 dias
    const { data: completed7d, error: completed7dError } = await supabase
      .from('workflow_tasks_v9')
      .select('id, is_overdue')
      .eq('organization_id', organizationId)
      .eq('status', 'completed')
      .gte('completed_at', sevenDaysAgo.toISOString());

    if (completed7dError) throw completed7dError;

    // Tasks concluídas nos últimos 30 dias
    const { data: completed30d, error: completed30dError } = await supabase
      .from('workflow_tasks_v9')
      .select('id, is_overdue')
      .eq('organization_id', organizationId)
      .eq('status', 'completed')
      .gte('completed_at', thirtyDaysAgo.toISOString());

    if (completed30dError) throw completed30dError;

    // Calcular métricas
    const tasks_overdue = tasks?.filter(t => t.is_overdue).length || 0;

    const tasks_due_today = tasks?.filter(t => {
      if (!t.sla_deadline) return false;
      const deadline = t.sla_deadline.split('T')[0];
      return deadline === today;
    }).length || 0;

    const sla_7d_total = completed7d?.length || 0;
    const sla_7d_on_time = completed7d?.filter(t => !t.is_overdue).length || 0;
    const sla_rate_7d = sla_7d_total > 0 ? (sla_7d_on_time / sla_7d_total) * 100 : 0;

    const sla_30d_total = completed30d?.length || 0;
    const sla_30d_on_time = completed30d?.filter(t => !t.is_overdue).length || 0;
    const sla_rate_30d = sla_30d_total > 0 ? (sla_30d_on_time / sla_30d_total) * 100 : 0;

    const throughput_7d = sla_7d_total;

    // Calcular WIP médio por usuário
    const activeUsers = new Set(tasks?.filter(t => t.assigned_to).map(t => t.assigned_to));
    const avg_wip_per_user = activeUsers.size > 0 ? (tasks?.length || 0) / activeUsers.size : 0;

    // Encontrar etapa com maior WIP
    const stageWip: Record<string, number> = {};
    tasks?.forEach((t: any) => {
      const name = t.stage?.name ?? t.stage_id ?? 'Sem etapa';
      stageWip[name] = (stageWip[name] || 0) + 1;
    });
    
    const most_critical_stage = Object.keys(stageWip).length > 0
      ? Object.entries(stageWip).sort((a, b) => b[1] - a[1])[0][0]
      : null;

    return {
      tasks_overdue,
      sla_rate_7d: Math.round(sla_rate_7d * 10) / 10,
      sla_rate_30d: Math.round(sla_rate_30d * 10) / 10,
      avg_wip_per_user: Math.round(avg_wip_per_user * 10) / 10,
      throughput_7d,
      most_critical_stage,
      tasks_due_today
    };
  } catch (error) {
    console.error('Erro ao buscar métricas operacionais:', error);
    return {
      tasks_overdue: 0,
      sla_rate_7d: 0,
      sla_rate_30d: 0,
      avg_wip_per_user: 0,
      throughput_7d: 0,
      most_critical_stage: null,
      tasks_due_today: 0
    };
  }
}

/**
 * Busca minhas tasks pendentes (para ANALISTA)
 */
export async function getMyTasks(userId: string, organizationId: string): Promise<MyTasksSummary> {
  try {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Buscar minhas tasks pendentes
    const { data: myTasks, error: myTasksError } = await supabase
      .from('workflow_tasks_v9')
      .select('id, stage_id, sla_deadline, is_overdue, stage:workflow_stages_v9(name)')
      .eq('organization_id', organizationId)
      .eq('assigned_to', userId)
      .in('status', ['pending', 'in_progress', 'paused'])
      .order('sla_deadline', { ascending: true, nullsFirst: false });

    if (myTasksError) throw myTasksError;

    // Buscar minhas tasks completadas nos últimos 7 dias
    const { data: myCompleted, error: myCompletedError } = await supabase
      .from('workflow_tasks_v9')
      .select('id, is_overdue')
      .eq('organization_id', organizationId)
      .eq('assigned_to', userId)
      .eq('status', 'completed')
      .gte('completed_at', sevenDaysAgo.toISOString());

    if (myCompletedError) throw myCompletedError;

    // Processar tasks pendentes
    const pending_tasks = (myTasks || []).slice(0, 5).map((task: any) => {
      let hours_until_deadline = null;
      if (task.sla_deadline) {
        const deadline = new Date(task.sla_deadline);
        hours_until_deadline = Math.round((deadline.getTime() - now.getTime()) / (1000 * 60 * 60));
      }

      return {
        id: task.id,
        task_name: task.stage?.name ?? task.stage_id ?? undefined,
        stage_name: task.stage?.name ?? task.stage_id ?? undefined,
        sla_deadline: task.sla_deadline,
        is_overdue: task.is_overdue,
        hours_until_deadline
      };
    });

    const my_overdue_count = myTasks?.filter(t => t.is_overdue).length || 0;

    // Calcular meu SLA 7 dias
    const my_completed_7d = myCompleted?.length || 0;
    const my_on_time_7d = myCompleted?.filter(t => !t.is_overdue).length || 0;
    const my_sla_rate_7d = my_completed_7d > 0 ? (my_on_time_7d / my_completed_7d) * 100 : 0;

    return {
      pending_tasks,
      my_overdue_count,
      my_sla_rate_7d: Math.round(my_sla_rate_7d * 10) / 10
    };
  } catch (error) {
    console.error('Erro ao buscar minhas tasks:', error);
    return {
      pending_tasks: [],
      my_overdue_count: 0,
      my_sla_rate_7d: 0
    };
  }
}

/**
 * Busca performance de usuários (para GESTOR)
 */
export async function getUsersPerformance(organizationId: string, limit: number = 3): Promise<UserPerformance[]> {
  try {
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

    // Buscar todas as tasks dos últimos 7 dias
    const { data: tasks, error: tasksError } = await supabase
      .from('workflow_tasks_v9')
      .select('assigned_to, status, is_overdue, completed_at')
      .eq('organization_id', organizationId)
      .not('assigned_to', 'is', null);

    if (tasksError) throw tasksError;

    // Buscar emails dos usuários (user_profiles pode não existir)
    const userIds = [...new Set(tasks?.map(t => t.assigned_to) || [])];
    let userEmailMap: Record<string, string> = {};
    if (userIds.length > 0) {
      try {
        const { data: users } = await supabase
          .from('user_profiles')
          .select('user_id, email')
          .in('user_id', userIds);
        users?.forEach(u => {
          userEmailMap[u.user_id] = u.email || 'Usuário sem email';
        });
      } catch {
        // user_profiles table may not exist — use IDs as fallback
      }
    }

    // Agrupar por usuário
    const userStats: Record<string, {
      completed_7d: number;
      on_time_7d: number;
      active_tasks: number;
      overdue_tasks: number;
    }> = {};

    tasks?.forEach(task => {
      if (!task.assigned_to) return;
      
      if (!userStats[task.assigned_to]) {
        userStats[task.assigned_to] = {
          completed_7d: 0,
          on_time_7d: 0,
          active_tasks: 0,
          overdue_tasks: 0
        };
      }

      if (task.status === 'completed' && task.completed_at) {
        const completedDate = new Date(task.completed_at);
        if (completedDate >= sevenDaysAgo) {
          userStats[task.assigned_to].completed_7d++;
          if (!task.is_overdue) {
            userStats[task.assigned_to].on_time_7d++;
          }
        }
      }

      if (task.status === 'pending' || task.status === 'in_progress') {
        userStats[task.assigned_to].active_tasks++;
        if (task.is_overdue) {
          userStats[task.assigned_to].overdue_tasks++;
        }
      }
    });

    // Calcular performance
    const performances: UserPerformance[] = Object.entries(userStats).map(([userId, stats]) => {
      const sla_rate = stats.completed_7d > 0 
        ? (stats.on_time_7d / stats.completed_7d) * 100 
        : 0;

      return {
        user_id: userId,
        user_email: userEmailMap[userId] || userId,
        sla_rate_7d: Math.round(sla_rate * 10) / 10,
        avg_wip: stats.active_tasks,
        tasks_completed_7d: stats.completed_7d,
        tasks_overdue: stats.overdue_tasks
      };
    });

    // Ordenar por menor SLA (piores performers primeiro)
    return performances
      .sort((a, b) => a.sla_rate_7d - b.sla_rate_7d)
      .slice(0, limit);
  } catch (error) {
    console.error('Erro ao buscar performance de usuários:', error);
    return [];
  }
}

/**
 * Busca performance de etapas
 */
export async function getStagePerformance(organizationId: string): Promise<StagePerformance[]> {
  try {
    const { data: tasks, error } = await supabase
      .from('workflow_tasks_v9')
      .select('stage_id, status, started_at, completed_at, is_overdue, stage:workflow_stages_v9(name)')
      .eq('organization_id', organizationId);

    if (error) throw error;

    // Agrupar por etapa
    const stageStats: Record<string, {
      active: number;
      completed: number;
      on_time: number;
      total_cycle_time: number;
      cycle_count: number;
    }> = {};

    tasks?.forEach((task: any) => {
      const stageName = task.stage?.name ?? task.stage_id ?? 'Sem etapa';
      if (!stageStats[stageName]) {
        stageStats[stageName] = {
          active: 0,
          completed: 0,
          on_time: 0,
          total_cycle_time: 0,
          cycle_count: 0
        };
      }

      if (task.status === 'pending' || task.status === 'in_progress') {
        stageStats[stageName].active++;
      }

      if (task.status === 'completed') {
        stageStats[stageName].completed++;
        if (!task.is_overdue) {
          stageStats[stageName].on_time++;
        }

        // Calcular cycle time
        if (task.started_at && task.completed_at) {
          const start = new Date(task.started_at);
          const end = new Date(task.completed_at);
          const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
          stageStats[stageName].total_cycle_time += hours;
          stageStats[stageName].cycle_count++;
        }
      }
    });

    // Converter para array
    return Object.entries(stageStats).map(([stage_name, stats]) => {
      const total_tasks = stats.active + stats.completed;
      const sla_rate = stats.completed > 0 
        ? (stats.on_time / stats.completed) * 100 
        : 0;
      const avg_cycle_time = stats.cycle_count > 0 
        ? stats.total_cycle_time / stats.cycle_count 
        : 0;

      return {
        stage_name,
        avg_wip: stats.active,
        avg_cycle_time_hours: Math.round(avg_cycle_time * 10) / 10,
        sla_rate: Math.round(sla_rate * 10) / 10,
        sla_sampled: stats.completed > 0,
        tasks_count: total_tasks
      };
    }).sort((a, b) => b.avg_wip - a.avg_wip);
  } catch (error) {
    console.error('Erro ao buscar performance de etapas:', error);
    return [];
  }
}

function buildDateSeries(days: number): Array<{ date: string; label: string }> {
  const labels: Array<{ date: string; label: string }> = [];
  const formatter = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
  const today = new Date();

  for (let index = days - 1; index >= 0; index -= 1) {
    const current = new Date(today);
    current.setHours(0, 0, 0, 0);
    current.setDate(current.getDate() - index);
    labels.push({
      date: current.toISOString().slice(0, 10),
      label: formatter.format(current),
    });
  }

  return labels;
}

export async function getOperationalSlaTrend(organizationId: string, days: number = 30): Promise<DailyMetricPoint[]> {
  try {
    const safeDays = Math.max(days, 1);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (safeDays - 1));

    const { data, error } = await supabase
      .from('workflow_tasks_v9')
      .select('completed_at, is_overdue')
      .eq('organization_id', organizationId)
      .eq('status', 'completed')
      .gte('completed_at', start.toISOString());

    if (error) throw error;

    const buckets = new Map<string, { total: number; onTime: number; late: number }>();
    for (const row of data ?? []) {
      if (!row.completed_at) continue;
      const dateKey = String(row.completed_at).slice(0, 10);
      const bucket = buckets.get(dateKey) ?? { total: 0, onTime: 0, late: 0 };
      bucket.total += 1;
      if (row.is_overdue) {
        bucket.late += 1;
      } else {
        bucket.onTime += 1;
      }
      buckets.set(dateKey, bucket);
    }

    return buildDateSeries(safeDays).map(({ date, label }) => {
      const bucket = buckets.get(date) ?? { total: 0, onTime: 0, late: 0 };
      const value = bucket.total > 0 ? (bucket.onTime / bucket.total) * 100 : 0;
      return {
        date,
        label,
        value: Math.round(value * 10) / 10,
        total: bucket.total,
        onTime: bucket.onTime,
        late: bucket.late,
      };
    });
  } catch (error) {
    console.error('Erro ao buscar tendencia diaria de SLA:', error);
    return buildDateSeries(Math.max(days, 1)).map(({ date, label }) => ({
      date,
      label,
      value: 0,
      total: 0,
      onTime: 0,
      late: 0,
    }));
  }
}

export async function getOperationalThroughputTrend(organizationId: string, days: number = 7): Promise<DailyMetricPoint[]> {
  try {
    const safeDays = Math.max(days, 1);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (safeDays - 1));

    const { data, error } = await supabase
      .from('workflow_tasks_v9')
      .select('completed_at')
      .eq('organization_id', organizationId)
      .eq('status', 'completed')
      .gte('completed_at', start.toISOString());

    if (error) throw error;

    const buckets = new Map<string, number>();
    for (const row of data ?? []) {
      if (!row.completed_at) continue;
      const dateKey = String(row.completed_at).slice(0, 10);
      buckets.set(dateKey, (buckets.get(dateKey) ?? 0) + 1);
    }

    return buildDateSeries(safeDays).map(({ date, label }) => ({
      date,
      label,
      value: buckets.get(date) ?? 0,
    }));
  } catch (error) {
    console.error('Erro ao buscar tendencia diaria de throughput:', error);
    return buildDateSeries(Math.max(days, 1)).map(({ date, label }) => ({
      date,
      label,
      value: 0,
    }));
  }
}
